import axios from 'axios';
import { extForMimeType } from './audioFormat';
import { loadStaySession } from './staySession';
import { loadCabinetSession } from './cabinetSession';
import { reportApiFailure, reportApiSuccess, reportFetchResponse } from './apiHealth';
import { CLIENT_KEY_HEADER, clientKey, clientKeyHeader } from './clientKey';

// Base URL of the FastAPI backend. Empty string = same origin (the backend
// serves the built frontend in the single-service deploy); set
// REACT_APP_BACKEND_URL only when the frontend is hosted separately.
// ⚑ L'adresse vit dans `lib/apiBase.js` depuis le 15/09/2026, et elle est
// RÉEXPORTÉE ici pour qu'aucun import existant n'ait à changer. La raison est
// écrite là-bas : une page qui ne veut que l'adresse ne doit pas tirer axios
// derrière elle — ce qui a failli arriver à la page d'attente.
// ⚠ `import` PUIS `export`, et non `export … from` : une réexportation seule
// ne crée AUCUNE liaison locale, si bien que `BACKEND_URL` devenait indéfini
// à l'intérieur de ce fichier — `bookletUrl` s'en sert pour bâtir l'URL du
// livret, et la suite l'a dit tout de suite.
import { API, BACKEND_URL } from './apiBase';

export { API, BACKEND_URL };

// ── Shared, gesture-unlocked audio element ─────────────────────────────
// iOS Safari only allows audio whose playback was authorized during a user
// gesture. Médard's voice arrives seconds after the tap (LLM + TTS), so we
// keep ONE persistent <audio> element and "unlock" it inside the tap by
// playing a silent clip. Later .play() calls on that same element are then
// permitted. Call unlockAudio() synchronously from every tap handler that
// can lead to Médard speaking (send button, mic button, suggestions…).
const SILENT_WAV =
    'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=';
let _sharedAudio = null;
let _audioUnlocked = false;

export function getSharedAudio() {
    if (!_sharedAudio) _sharedAudio = new Audio();
    return _sharedAudio;
}

export function unlockAudio() {
    const audio = getSharedAudio();
    if (_audioUnlocked) return audio;
    try {
        // CRITICAL (iOS): unlock with an UNMUTED silent clip. A *muted* play is
        // allowed on iOS WITHOUT a user gesture, so it never consumes the tap's
        // activation and does NOT authorize later real (unmuted) playback — the
        // element stays locked and Médard's voice, arriving seconds later, is
        // rejected with NotAllowedError (mobile-only silence). The SILENT_WAV is
        // genuinely zero samples, so playing it unmuted inside the tap is
        // inaudible AND blesses the element for every subsequent .play().
        audio.muted = false;
        audio.volume = 1;
        audio.src = SILENT_WAV;
        const p = audio.play();
        if (p && typeof p.then === 'function') {
            p.then(() => {
                audio.pause();
                try { audio.currentTime = 0; } catch { /* ignore */ }
                _audioUnlocked = true;
            }).catch(() => {
                /* not blessed — will retry on the next gesture */
            });
        } else {
            _audioUnlocked = true;
        }
    } catch {
        /* retry on the next gesture */
    }
    return audio;
}

const http = axios.create({
    baseURL: API,
    timeout: 120000,
});

// ── « LA PORTE D'ENTRÉE » — le délai propre aux appels qui OUVRENT un écran ──
//
// Le `timeout: 120000` ci-dessus reste : il sert aux envois de photos et à la
// synthèse vocale, qui ont le DROIT d'être longs (un clip ElevenLabs de
// quarante secondes se fabrique lentement, et l'invité, lui, voit déjà
// quelque chose pendant ce temps-là). Mais deux minutes appliquées à la
// PREMIÈRE requête d'un écran plein cadre, c'est deux minutes pendant
// lesquelles le mur ne peut rien dire d'autre que « j'attends ». Personne
// n'attend deux minutes devant un mur : on conclut que c'est cassé, et on a
// raison de le conclure.
//
// LE CHIFFRE, ET POURQUOI CELUI-LÀ. Dix secondes. Ce n'est pas une préférence
// esthétique : c'est le moment où « ça charge » bascule en « c'est en panne »
// dans la tête de quelqu'un qui regarde un écran sans savoir ce qu'il y a
// derrière. En deçà, on abandonne un serveur qui allait répondre ; au-delà, on
// a déjà perdu l'invité, et le repli qu'on lui sert arrive trop tard pour
// qu'il le lise comme une politesse plutôt que comme une confirmation de
// panne. Dix secondes, donc, ET un mur qui PARLE pendant ces dix secondes
// (c'est l'autre moitié du travail, côté écran : voir ProjectionConteScreen).
//
// LA REPRISE. Une coupure d'une seconde — le téléphone qui saute du Wi-Fi du
// hall à la 4G en montant l'escalier — ne doit pas coûter la soirée : ce
// n'est même pas un délai dépassé, c'est un échec réseau IMMÉDIAT, et une
// seconde tentative 400 ms plus tard gagne sans rien coûter au budget. C'est
// pour ce cas-là que la reprise existe. Elle est donc bornée par une
// ÉCHÉANCE, pas par un second délai plein : deux tentatives de dix secondes
// feraient vingt secondes, c'est-à-dire exactement la faute qu'on corrige.
// La première tentative a son propre plafond (sept secondes — un réveil à
// froid Railway prend typiquement trois à six secondes, on lui laisse sa
// chance), et la seconde reçoit ce qui reste du budget.
export const DOOR_BUDGET_MS = 10000;   // l'échéance totale avant le repli honnête
export const DOOR_FIRST_MS = 7000;     // le plafond de la PREMIÈRE tentative
export const DOOR_RETRY_GAP_MS = 400;  // le souffle entre les deux essais
export const DOOR_MIN_RETRY_MS = 1200; // en deçà, il ne reste pas de quoi réessayer

/** Ce qui mérite une seconde tentative — et rien d'autre.
 *
 *  Pas de réponse du tout (délai dépassé, réseau coupé, DNS, CORS) : le
 *  second essai peut gagner. Un 5xx aussi : un serveur qui se réveille
 *  répond 502/503 avant de répondre 200. Un 404 ou un 400, en revanche,
 *  répondra exactement la même chose la seconde fois — réessayer ne ferait
 *  que voler la moitié du budget d'attente de l'invité. */
const worthRetrying = (err) => {
    const status = err?.response?.status;
    if (status === undefined || status === null) return true;
    return status >= 500;
};

const wait = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

const nowMs = () => (typeof performance !== 'undefined' && performance.now
    ? performance.now() : Date.now());

/**
 * GET sous échéance — le transport des requêtes qui ouvrent une porte.
 *
 * Rejette avec l'erreur de la DERNIÈRE tentative (l'appelant reçoit ce qu'il
 * recevait avant : observer et réessayer ne changent pas le contrat), au plus
 * tard DOOR_BUDGET_MS après le premier octet envoyé.
 */
/**
 * ⚑ CE QUI REND UNE TENTATIVE DISTINCTE DE CELLE QU'ELLE REMPLACE (12/09/2026).
 *
 * Mesuré au navigateur, sur un serveur qui ACCEPTE la connexion et ne répond
 * jamais — le cas exact pour lequel la porte honnête existe :
 *
 *   · une requête sur une adresse qui ne diffère que d'un paramètre : **5 ms** ;
 *   · une requête sur l'adresse IDENTIQUE à deux tentatives déjà avortées :
 *     **20 006 ms**.
 *
 * Le navigateur met en file les requêtes de même adresse derrière celles qui
 * pendent encore — c'est ce qui lui permet, d'ordinaire, de n'en faire qu'une
 * et de partager la réponse. Ici cela retourne la mécanique contre nous : la
 * tentative qui doit RÉPARER la panne attend derrière la panne.
 *
 * Le remède est un paramètre qui ne sert qu'à dire « celle-ci n'est pas
 * celle-là ». Il n'est posé QUE sur une tentative qui suit un échec : un
 * chargement normal garde son adresse nue, donc sa mise en cache.
 */
let _tentative = 0;
function distincte(config) {
    _tentative += 1;
    return { ...config, params: { ...(config.params || {}), _essai: _tentative } };
}

async function doorGet(path, config = {}) {
    const deadline = nowMs() + DOOR_BUDGET_MS;
    const first = Math.min(DOOR_FIRST_MS, DOOR_BUDGET_MS);
    // `frais` : cet appel SUIT un échec (un doigt sur la carte d'aveu). Sa
    // première tentative doit déjà être distincte, sinon elle fait la queue
    // derrière les tentatives avortées de l'appel précédent.
    const { frais, ...reste } = config;
    const depart = frais ? distincte(reste) : reste;
    try {
        return await http.get(path, { ...depart, timeout: first });
    } catch (err) {
        if (!worthRetrying(err) || deadline - nowMs() - DOOR_RETRY_GAP_MS < DOOR_MIN_RETRY_MS) throw err;
        await wait(DOOR_RETRY_GAP_MS);
        // Ce qui reste se lit APRÈS le souffle, pas avant : un `setTimeout`
        // de 400 ms en rend 420 sur une machine chargée, et un délai calculé
        // avant le souffle débordait l'échéance d'autant (revue du 10/09 :
        // 10 044 et 10 048 ms mesurés pour un budget de 10 000). L'échéance
        // est une échéance, pas une addition.
        const left = Math.max(1, Math.round(deadline - nowMs()));
        // Et la seconde tentative est TOUJOURS distincte de la première :
        // sans cela elle attend la fin de celle qu'elle remplace.
        return http.get(path, { ...distincte(reste), timeout: left });
    }
}

// Toutes les requêtes de CONTENU passent ici — c'est donc ici qu'on apprend,
// sans rien sonder, que le serveur ne répond plus. `navigator.onLine` ne le
// dira jamais : le téléphone de l'invité va très bien. Voir lib/apiHealth.js
// pour ce qui compte comme panne (et surtout ce qui n'y compte pas).
//
// Pose DÉFENSIVE, et c'est un choix, pas une timidité : c'est du code qui
// s'exécute à l'IMPORT du module. Une instance axios sans `interceptors` (une
// doublure de test, une version d'axios qui bouge) ferait alors tomber tout
// medardApi — donc toute l'app — pour une couche qui ne fait qu'OBSERVER.
// Le ruban vaut mieux que rien ; il ne vaut pas l'app. Neuf suites de tests
// l'ont démontré avant que la question ne se pose en production.
if (http?.interceptors?.response?.use) {
    http.interceptors.response.use(
        (res) => { reportApiSuccess(); return res; },
        (err) => { reportApiFailure(err); return Promise.reject(err); },
    );
}

// LE NOM DE L'APPAREIL SUR CHAQUE ÉCRITURE (12/10/2026) — voir `clientKey.js`.
// Le serveur compte les portes coûteuses par appareil, et non plus par
// adresse : derrière le Wi-Fi du Manoir, tous les invités partagent la même.
// Toutes ces portes sont des POST ; une lecture (GET) ne porte donc rien, ce
// qui la garde « simple » au sens CORS — pas de requête préalable ajoutée sur
// les pages de contenu si le front est un jour servi d'une autre origine.
// Pose défensive, même raison que l'intercepteur ci-dessus.
export function withClientKeyConfig(config) {
    const method = String(config?.method || 'get').toLowerCase();
    if (method === 'get' || method === 'head') return config;
    if (config.headers && typeof config.headers.set === 'function') {
        config.headers.set(CLIENT_KEY_HEADER, clientKey());
        return config;
    }
    return { ...config, headers: { ...(config.headers || {}), ...clientKeyHeader() } };
}

if (http?.interceptors?.request?.use) {
    http.interceptors.request.use(withClientKeyConfig);
}

// La même règle pour `fetch` nu (la voix, le flux, l'Œil) : un POST porte le nom.
export function withClientKeyInit(init) {
    const method = String(init?.method || 'GET').toUpperCase();
    if (method === 'GET' || method === 'HEAD') return init;
    const headers = init.headers;
    if (headers && typeof headers.set === 'function') {
        headers.set(CLIENT_KEY_HEADER, clientKey());
        return init;
    }
    return { ...init, headers: { ...(headers || {}), ...clientKeyHeader() } };
}

// Resilience (Law 3 — serve, never break): a malformed-but-200 response (an
// object where a list is expected) must NEVER reach the UI as a non-array — a
// downstream `(x || []).slice/.map` would throw and drop the WHOLE app to the
// root error boundary. Array-returning endpoints funnel through this so a bad
// payload degrades to an empty list, not a crash.
const asArray = (v) => (Array.isArray(v) ? v : []);

// ⚑ UN REFUS DE LA VOIX PORTE SON CODE (25/10/2026). Le message reste celui
// d'hier ; ce qui change, c'est que `.status` voyage avec lui. Le guide de la
// balade doit pouvoir dire POURQUOI Médard se tait, et un 409 (« ce texte de
// fiche n'est pas encore semé », backend/fixed_speech.py) ne se dit pas comme
// une panne : l'un attend un semis, l'autre une reprise. `apiHealth` lisait
// déjà cette forme nue pour les chemins de la voix.
const speakFailure = (status) =>
    Object.assign(new Error(`speak failed: ${status}`), { status });

/**
 * `fetch` qui rend compte de la santé du serveur.
 *
 * L'intercepteur axios ci-dessus ne voit que les requêtes de CONTENU. Les
 * chemins de la voix — poser une question, faire parler Médard, scanner —
 * passent par `fetch` nu et lui échappaient : un invité qui arrive sur une
 * coquille servie par le service worker et se contente de PARLER à Médard,
 * c'est-à-dire l'usage principal de cette app, ne déclenchait aucun ruban
 * alors que rien ne lui répondait.
 *
 * L'erreur est toujours relancée telle quelle : observer ne doit jamais
 * changer ce que l'appelant reçoit.
 */
async function watchedFetch(input, init) {
    try {
        const res = await fetch(input, init ? withClientKeyInit(init) : init);
        reportFetchResponse(res);
        return res;
    } catch (err) {
        reportApiFailure(err);
        throw err;
    }
}

// « La Clef du Manoir » transport (cœur-du-réacteur C5): when an active stay
// lives on this device (localStorage, self-expiring at checkout + grace), its
// guest token rides every concierge-relevant request as `stay_token`. The
// SERVER is the only truth — it re-verifies (decode + DB re-check) and grants
// the clef tier; client hints stay display-only. No session → the field is
// simply absent (a public request, byte-identical to before). Never throws.
export function withStayToken(body) {
    try {
        const token = loadStaySession()?.token;
        return token ? { ...body, stay_token: token } : body;
    } catch {
        return body;
    }
}

// « Le Cabinet d'étude » transport (access circle 2) — the researcher's twin of
// withStayToken: an active admission rides the ASK requests as `cabinet_token`,
// which is the only field the backend's _professeur_gate reads for circle 2.
//
// Deliberately NOT applied to reserve / managerRelay: a Cabinet code admits its
// holder to the scholarly register, not to the house. A researcher's token has
// no business on a reservation or a concierge relay — those speak of a stay,
// and a scholar has none. The SERVER is the only truth here too: it decodes and
// re-reads the DB on every question, so a revoked code loses the register at
// once. No session → the field is simply absent. Never throws.
export function withCabinetToken(body) {
    try {
        const token = loadCabinetSession()?.token;
        return token ? { ...body, cabinet_token: token } : body;
    } catch {
        return body;
    }
}

/** Both doors of the Professeur on one request — la Clef and le Cabinet.
 *  Each field is independently present or absent; neither implies the other. */
const withAccessTokens = (body) => withCabinetToken(withStayToken(body));

/** LA PREUVE DE PROPRIÉTÉ D'UN SALON, en en-tête et jamais ailleurs.
 *
 *  `backend/session_guard.py` la lit dans `X-Medard-Session-Secret`. Les
 *  portes se comptent en deux familles, et la seconde est née le 19/08 :
 *
 *    · celles qui RENDENT la mémoire d'une session — le fil, le passeport, le
 *      jeton du livret, le courriel du livret, le tampon de l'Œil
 *      (`may_read_session`) ;
 *    · celles qui ÉCRIVENT ou DIFFUSENT dans un salon sans rien en lire —
 *      l'annonce de séjour (`announceStay`) et le relais vers Vincent
 *      (`managerRelay`), toutes deux sous `may_address_room`.
 *
 *  Cette liste-ci n'est pas décorative : c'est celle que
 *  `sessionProofReachesEveryDoor.test.js` tient à jour, et son retard d'une
 *  vague a suffi à livrer un chapitre entier du Livret sans preuve. Toute
 *  porte ajoutée à `session_guard` s'inscrit ICI et LÀ-BAS.
 *
 *  Elle ne passe PAS par l'URL : une URL entre dans les journaux d'accès,
 *  l'historique du navigateur et le `Referer` — exactement les canaux par
 *  lesquels l'identifiant fuyait, et qu'on ne rouvre pas d'un cran plus loin
 *  pour la preuve elle-même.
 *
 *  Pas de preuve → pas d'en-tête, jamais une chaîne vide : le serveur refuse
 *  de la même façon, mais un en-tête vide ferait croire à une preuve. */
export const SESSION_SECRET_HEADER = 'X-Medard-Session-Secret';

export function sessionProofHeader(sessionSecret) {
    return sessionSecret ? { [SESSION_SECRET_HEADER]: sessionSecret } : {};
}

// ⚑ LA LISTE ET LA FICHE D'UN MONUMENT S'ÉCRIVENT UNE SEULE FOIS (25/10/2026).
// Le service worker garde une réponse sous son adresse EXACTE, paramètres
// compris. La balade relit ces copies pour savoir ce qui voyagera hors ligne
// (lib/garderLaBalade.js) : si la page recomposait l'adresse de son côté, un
// paramètre déplacé suffirait pour qu'elle cherche une copie que personne n'a
// rangée là — et elle se tairait sans savoir pourquoi, ou pire, conclurait à
// tort. Les deux appels et la relecture partent donc du même descripteur, et
// l'adresse sort du sérialiseur d'axios lui-même : celui qui l'envoie.
export const monumentDetailRequest = (id, lang = 'fr') => ({
    path: `/medard/monuments/${id}`,
    params: { lang },
});
export const listMonumentsRequest = (lang = 'fr', vue = null) => ({
    path: '/medard/monuments',
    params: vue ? { lang, vue } : { lang },
});
export const adresseGardee = ({ path, params }) => http.getUri({ url: path, params });

export const medardApi = {
    // ⚑ LES LECTURES QUI OUVRENT UNE PAGE PASSENT PAR LA PORTE (10/09/2026).
    // Les quatre listes (monuments, trésors, personnages, événements), les
    // cinq détails (les quatre familles et la suite), plus les suites et les
    // balades : chacune est la PREMIÈRE requête d'une page du site, et chacune
    // attendait les deux minutes du transport général. Mesuré au navigateur
    // sur une requête qui pend : `site-loading` pendant 120,58 s avant le
    // premier aveu (audit, robustesse n°1). Le module plaidait contre ce
    // chiffre vingt lignes plus haut et ne l'appliquait qu'à `contes`.
    // `medardApi.doorTimeout.test.js` tient désormais la liste.
    listMonuments: (lang = 'fr', vue = null) => {
        const d = listMonumentsRequest(lang, vue);
        return doorGet(d.path, { params: d.params }).then((r) => asArray(r.data));
    },

    // `lang` matters: the detail endpoint resolves the date line and the
    // key-fact labels into the guest's language, and without it every fiche
    // came back French for a German or Spanish reader.
    monumentDetail: (id, lang = 'fr') => {
        const d = monumentDetailRequest(id, lang);
        return doorGet(d.path, { params: d.params }).then((r) => r.data);
    },

    /**
     * The verified historical figures of Autun / Augustodunum
     * (canon/personnages). The set is small (7), so the endpoint returns
     * everything the portrait gallery needs in one call: id, name_*, role_*,
     * epoch, confidence, key_facts, portrait_*, sources, monument_id, plus a
     * verified Wikimedia image (image / verified_image) and an honest
     * image_note_* where the image is a modern imagining or the figure's work.
     */
    personnages: (lang = 'fr', vue = null) =>
        doorGet('/medard/personnages', { params: vue ? { lang, vue } : { lang } }).then((r) => asArray(r.data)),

    /**
     * ONE figure, whole (GET /medard/personnages/{id}?lang=): the raw canon
     * entry under the same resolved projection the list serves — a detail
     * never contradicts its card. The website's figure page reads it
     * (SITE_WEB_DESKTOP.md §4, vague 2b).
     */
    personnageDetail: (id, lang = 'fr') =>
        doorGet(`/medard/personnages/${encodeURIComponent(id)}`, { params: { lang } }).then((r) => r.data),

    /**
     * « Les trésors d'Autun » — the verified treasures the city has yielded
     * or still keeps (canon/tresors). Same contract shape as the figures:
     * id, name_fr/en, kind_fr/en, epoch, confidence, key_facts, recit_fr/en,
     * sources, monument_id, ar_id, plus the verified Wikimedia image
     * (image / verified_image) and an honest image_note_* when the image is
     * not the treasure itself.
     */
    tresors: (lang = 'fr', vue = null) =>
        doorGet('/medard/tresors', { params: vue ? { lang, vue } : { lang } }).then((r) => asArray(r.data)),

    /** ONE treasure, whole (GET /medard/tresors/{id}?lang=) — same contract as personnageDetail. */
    tresorDetail: (id, lang = 'fr') =>
        doorGet(`/medard/tresors/${encodeURIComponent(id)}`, { params: { lang } }).then((r) => r.data),

    /**
     * « Fêtes & Spectacles » — la cité vivante (canon/evenements). The item
     * shape is IDENTICAL to the treasures one: id, name_fr/en, kind_fr/en,
     * kind, epoch, confidence, key_facts, recit_fr/en, recit, sources,
     * monument_id, plus the imagery (image / photo / photo_credit_* ) and an
     * honest image_note_* when the picture is not the event itself.
     *
     * `kind` / `recit` carry the resolved translation for ES/IT/DE/ZH/NL and
     * are null for FR/EN (the UI falls back to `*_fr` / `*_en`, exactly as the
     * treasures do); `epoch` is already resolved for the requested lang.
     *
     * Same resilience contract as every other list endpoint: a malformed or
     * null payload coerces to [] rather than throwing (Law 3 — never break).
     */
    listEvenements: (lang = 'fr', vue = null) =>
        doorGet('/medard/evenements', { params: vue ? { lang, vue } : { lang } }).then((r) => asArray(r.data)),

    /**
     * ONE événement, whole (GET /medard/evenements/{id}?lang=): the list's
     * card plus the raw canon (`knowledge_asof` above all, which the list
     * drops). Born 07/09/2026 with the website's fête page.
     */
    evenementDetail: (id, lang = 'fr') =>
        doorGet(`/medard/evenements/${encodeURIComponent(id)}`, { params: { lang } }).then((r) => r.data),

    /**
     * « La frise des époques » — les neuf couches d'Autun (plus le tiroir
     * nommé « Hors du temps ») et l'appartenance de chacune des fiches datées
     * du canon.
     *
     * UN aller-retour pour TROIS écrans : la bande en tête d'Explorer a
     * besoin des comptes, une couche ouverte de ses fiches, une fiche ouverte
     * de savoir à quelles couches elle appartient. Les comptes descendent du
     * canon — aucun nombre n'est écrit dans l'écran.
     *
     * Forme : { couches: [{key, debut, fin, total, familles}],
     *           fiches:  [{id, famille, name_fr, name_en, dateline, image,
     *                      couches, certitude, attribution_debattue}] }
     *
     * Même contrat de résilience que les autres listes : une charge nulle ou
     * malformée retombe sur des listes vides plutôt que de jeter (Loi 3) — la
     * bande ne se rend simplement pas, et l'onglet Explorer reste entier.
     */
    frise: (lang = 'fr') =>
        http.get('/medard/frise', { params: { lang } }).then((r) => ({
            couches: asArray(r.data?.couches),
            fiches: asArray(r.data?.fiches),
        })),

    directions: (id) => http.get(`/medard/directions/${id}`).then((r) => r.data),

    /**
     * « La Bibliothèque de Médard » — the owner's children's-book collection
     * (Les Contes de Médard + Les Petits Trésors), plus the ONE readable
     * digital sample. Content lives verbatim in versioned markdown under
     * /lore/contes; the endpoint returns it structured:
     *   { intro, collections:[{cycles|books}], phase2, featured, books }.
     * The books are manuscripts in preparation (« À paraître » / « À lire »):
     * the payload NEVER carries a price / ISBN / availability field (Loi 1 +
     * the « never commit money » guardrail). See BibliothequeView / ConteReader.
     *
     * C'est L'APPEL QUI OUVRE LE MUR : `?display=conte` ne peut rien afficher
     * avant lui. Il passe donc par `doorGet` — dix secondes d'échéance et une
     * reprise, au lieu des deux minutes du transport général. Le même appel
     * ouvre la Bibliothèque du téléphone (BibliothequeView), qui attendait
     * derrière le même noir ; c'est la même porte, une couche plus bas.
     */
    contes: (lang = 'fr', frais = false) =>
        doorGet('/medard/contes', { params: { lang }, frais }).then((r) => r.data),

    manoir: () => http.get('/medard/manoir').then((r) => r.data),

    /** The 8 thematic suites of the Manoir (id, name, floor, theme…). The
     *  editorial `theme` is resolved into `lang` (ES/IT/DE/ZH) server-side. */
    suites: (lang = 'fr') =>
        doorGet('/medard/suites', { params: { lang } }).then((r) => asArray(r.data)),

    /** ONE suite, whole: narrative, anecdotes, palette, AR/VR experiences. */
    suite: (id, lang = 'fr') =>
        doorGet(`/medard/suites/${encodeURIComponent(id)}`, { params: { lang } }).then((r) => r.data),

    /**
     * The five curated signature itineraries (Série « Poétique éduen »,
     * editorial content in /lore/tours) with stop GPS resolved server-side.
     */
    tours: (lang = 'fr') =>
        doorGet('/medard/tours', { params: { lang } }).then((r) => asArray(r.data)),

    /** Range Rover availability — Law-1 honesty gate for the parcours that need it. */
    rangeRover: () => http.get('/medard/range-rover').then((r) => r.data),

    /**
     * Public Médard media gallery (Higgsfield films & visuals).
     * GET /medard/media?suite_id=<id> → { media: [...] } or a bare list of
     * { id, url, kind: 'image'|'video', caption, suite_id, monument_id }.
     * Callers normalize both shapes via lib/suiteMedia.normalizeMediaResponse.
     */
    media: ({ suiteId } = {}) =>
        http
            .get('/medard/media', { params: suiteId ? { suite_id: suiteId } : {} })
            .then((r) => r.data),

    /** Mint a pairing room — the SERVER strikes it, the screen asks before it
     *  prints (19/08/2026).
     *
     *  A screen that shows a pairing QR publishes a room id. While that id was
     *  minted CLIENT-side it stayed unknown to the backend registry until the
     *  first question, and the guard adopted it for whoever presented it first
     *  — so a stranger who read the id anywhere got the room, a working secret
     *  and a passport stamp in it (see backend/session_guard.py). A room the
     *  server did not strike is no longer adoptable, so a QR must carry a pair
     *  that came from HERE.
     *
     *  Writes nothing in the database (the secret is a self-authenticating
     *  token), but it is rate-limited all the same — an unauthenticated public
     *  door always is. Callers must therefore tolerate a refusal: a screen
     *  without a room shows its waiting line and retries; it never prints a QR
     *  nobody answers in. */
    mintRoom: () =>
        http.post('/medard/room').then((r) => ({
            sessionId: r.data?.session_id || null,
            sessionSecret: r.data?.session_secret || null,
        })),

    /** Visit passport for a session — stamps collected while exploring.
     *
     *  `sessionSecret` is the room's proof of ownership. Since 18/08 this
     *  door answers 404 without it: a passport says WHERE a guest went and on
     *  which day, and it is not handed over against an id (see
     *  backend/session_guard.py). The proof travels in a HEADER, never in the
     *  URL — an address bar reaches access logs, history and `Referer`, which
     *  are the very channels the guard closes. */
    passport: (sessionId, sessionSecret = null) =>
        http
            .get(`/medard/sessions/${encodeURIComponent(sessionId)}/passport`,
                 { headers: sessionProofHeader(sessionSecret) })
            .then((r) => r.data),

    // Concierge
    restaurants: () => http.get('/medard/concierge/restaurants').then((r) => asArray(r.data)),
    taxis: () => http.get('/medard/concierge/taxis').then((r) => asArray(r.data)),
    tourism: () => http.get('/medard/concierge/tourism').then((r) => r.data),
    // Public Manoir contact channels —
    // { whatsapp: <E.164 digits> | null, booking_url: <http(s) URL> | null }.
    // Each stays null until its env var (MEDARD_WHATSAPP_NUMBER /
    // MEDARD_BOOKING_URL) is configured server-side — never invented here.
    contact: () => http.get('/medard/contact').then((r) => r.data),

    /**
     * Relay a guest request to the human team (logs to Mongo + emails the
     * on-duty manager). Contract:
     *   { reason: 'urgency'|'reservation'|'special_request'|'general',
     *     summary: <str>, visitor_name?, visitor_phone?, visitor_email?,
     *     session_id?, language? }.
     * Constitution: the team confirms every request — nothing is ever booked
     * or charged from the app. This only carries the guest's words to a human.
     */
    /* `sessionSecret` prouve le SALON revendiqué par `payload.session_id`
     * (19/08/2026). Le relais n'échoue JAMAIS sans elle — il reste le canal
     * d'urgence de la maison, et un visiteur de réception n'a pas de salon du
     * tout : ce qui se perd, c'est le RATTACHEMENT de la demande à la
     * conversation, que le journal d'audit inscrit alors sous
     * `claimed_session_id` avec `session_proven: false`. Sans cette preuve,
     * n'importe qui pouvait faire figurer sa demande sous le salon d'un autre
     * invité, dans le journal que Vincent lit. */
    managerRelay: (payload, sessionSecret = null) =>
        http
            .post('/medard/manager/relay', withStayToken(payload),
                  { headers: sessionProofHeader(sessionSecret) })
            .then((r) => r.data),

    // Public front-end config, read from the server environment:
    //   { googleMapsApiKey: <str>, mapsDefault: 'google' | 'leaflet' }.
    // « Autun en 3D » reads this on mount — the Google 3D browser key and the
    // mapsDefault cost kill-switch (MEDARD_MAPS_DEFAULT on Railway). Any
    // server-served value stays in the env, NEVER hardcoded here.
    config: () => http.get('/config').then((r) => r.data),

    /**
     * The Autun sky right now — real Open-Meteo data, 30-min cached
     * server-side. Contract:
     *   { weather: { temp_c, code, label_key, high_c, low_c } | null }.
     * `label_key` is an i18n key (weatherClear…weatherStorm) translated
     * client-side (×5). null whenever the upstream gave nothing — the UI
     * then renders NO weather line (Law 1: never a stale or invented sky).
     */
    weather: () => http.get('/medard/weather').then((r) => r.data),

    /**
     * « Ma Clef du Manoir » — claim a stay code issued by the back-office
     * (PRODUCT_STRATEGY §1.3). Success → {ok, suite_id, guest_name,
     * guest_name_is_surname, checkout, token} (a guest JWT that can never
     * open the admin cockpit; the flag is true for a stay posted by the PMS).
     * The backend answers ONE uniform 401 for every failure mode (unknown /
     * revoked / expired / surname mismatch) — no code-enumeration oracle —
     * so callers map 401 to « wrong code » and 5xx/network to « retry ».
     */

    /** Salut nominatif de la suite : le téléphone qui a réclamé un séjour
     *  l'annonce au salon appairé. Le salon est en LECTURE SEULE côté socket —
     *  l'annonce passe donc par HTTP avec le jeton signé, et c'est le serveur
     *  qui compose et diffuse le message après vérification.
     *
     *  DEUX PREUVES, PAS UNE (19/08/2026). Le jeton de séjour prouve QUI
     *  arrive ; `sessionSecret` prouve DANS QUEL SALON on a le droit de le
     *  dire. Sans la seconde, n'importe quel porteur d'un séjour valable
     *  poussait un salut nominatif sur l'écran de n'importe quel salon dont il
     *  avait lu l'identifiant — c'était le dernier chemin pour écrire sur
     *  l'écran d'un autre (backend/session_guard.py, `may_address_room`).
     *
     *  Le téléphone qui a scanné le QR d'un écran porte les DEUX moitiés du
     *  couple frappé par le serveur (`pairingSession.js`, SECRET_PARAM), donc
     *  l'accueil nominatif ne perd rien. Sans secret, le serveur répond 404 et
     *  l'appelant l'avale : un salut perdu n'est jamais une panne visible. */
    announceStay: (sessionId, sessionSecret = null) =>
        http
            .post('/medard/stay/announce', {
                token: loadStaySession()?.token || '',
                session_id: sessionId,
            }, { headers: sessionProofHeader(sessionSecret) })
            .then((r) => r.data),
    claimStay: ({ code, surname = null }) =>
        http
            .post('/medard/stay/claim', { code, surname: surname || null })
            .then((r) => r.data),

    /**
     * Le mot de la porte, tant que le Manoir n'a pas ouvert (15/09/2026).
     * Le mot est comparé au SERVEUR (`backend/site_acces.py`) — il n'entre
     * jamais dans ce paquet, où il serait lisible en trois clics.
     * 401 = ce mot n'ouvre pas ; tout le reste est une panne, et les deux ne
     * se disent pas de la même façon à quelqu'un devant une porte.
     */
    siteAcces: (motDePasse) =>
        http
            .post('/medard/site/acces', { mot_de_passe: motDePasse })
            .then((r) => r.data),

    /**
     * « Le Cabinet d'étude » — claim a researcher code (access circle 2,
     * backend/cabinet_service.py). Success → {ok, holder_name, institution,
     * expires_at, token}: a token that opens the SCHOLARLY REGISTER and
     * nothing else — never a suite, never the private concierge.
     *
     * Three answers, three different sentences (the door is a door, not an
     * oracle):
     *   • 401 — ONE uniform rejection for unknown / revoked / expired, so the
     *     endpoint cannot be used to enumerate codes → « this code opens
     *     nothing »;
     *   • 503 — the code could not be LOOKED UP at all → « we cannot verify
     *     just now », which is emphatically NOT « your code is wrong »;
     *   • anything else / network → the same unavailable register.
     */
    claimCabinet: ({ code }) =>
        http
            .post('/medard/cabinet/claim', { code })
            .then((r) => r.data),

    /**
     * « La Lanterne de Médard » — light a coffret code (étape 6, lot 2).
     * Success → {ok, token, label, expires_at}: the house recognises the
     * Lanterne on this device — `expires_at` null means lit for life. Purely
     * additive plumbing: a lit Lanterne is neither a stay nor a Cabinet
     * admission, and no existing access changes.
     *
     * Same three answers as claimStay / claimCabinet (the door is a door,
     * not an oracle):
     *   • 401 — ONE uniform rejection for unknown / revoked / expired, so
     *     the endpoint cannot be used to enumerate codes → « this lantern
     *     does not light »;
     *   • 503 — the code could not be LOOKED UP at all → « we cannot verify
     *     just now », which is emphatically NOT « your code is wrong »;
     *   • anything else / network → the same honest unavailability.
     */
    claimLantern: (code) =>
        http
            .post('/medard/lantern/claim', { code })
            .then((r) => r.data),

    /**
     * « Les dossiers d'énigmes » — the Cabinet's étage 2 (V0). Admitted
     * researchers only: the signed Cabinet token rides the request and is
     * re-checked against the DB server-side (a revoked code loses the
     * workshop on the NEXT call). 401 = the token opens nothing; anything
     * else = the workshop is unreachable — different sentences (Loi 1).
     */
    cabinetDossiers: ({ token }) =>
        http
            .post('/medard/cabinet/dossiers', { token })
            .then((r) => r.data),
    randomLegend: (lang = 'fr') =>
        http.get('/medard/concierge/legends', { params: { lang } }).then((r) => r.data),

    /** Live agenda fetched from the Office de Tourisme du Grand Autunois Morvan. */
    events: ({ refresh = false, limit = 20 } = {}) =>
        http
            .get('/medard/events', { params: { refresh, limit } })
            .then((r) => r.data),

    // ── Le livret du voyageur, en DEUX temps ────────────────────────────
    //
    // Le téléchargement est un `<a href download>` : c'est le navigateur qui
    // émet la requête, et aucun code ne peut y ajouter d'en-tête. La preuve
    // de propriété ne peut donc pas voyager comme sur les autres portes — et
    // la mettre en paramètre d'URL serait pire que le défaut d'origine (le
    // secret PERMANENT d'un salon déposé dans les journaux d'accès et le
    // `Referer`, au lieu de son identifiant).
    //
    // Alors : ce POST porte la preuve et rend un JETON à usage unique et de
    // courte durée, puis `bookletUrl` construit le lien qui le consomme. La
    // langue et le prénom sont fixés ici, sous preuve, et voyagent dans le
    // jeton — le lien ne porte plus le prénom d'un invité jusque dans un
    // journal d'accès.
    bookletTicket: ({ sessionId, sessionSecret = null, lang = 'fr', name = '' }) =>
        http
            .post(`/medard/sessions/${encodeURIComponent(sessionId)}/booklet-ticket`,
                  { lang, visitor_name: name || null },
                  { headers: sessionProofHeader(sessionSecret) })
            .then((r) => r.data),

    // Le lien de téléchargement, construit AUTOUR d'un jeton déjà frappé.
    // Sans jeton il n'y a pas de lien : rendre une URL nue redonnerait
    // exactement la porte ouverte qu'on vient de fermer.
    bookletUrl: ({ sessionId, ticket }) => {
        if (!ticket) return null;
        const u = new URL(
            `${API}/medard/sessions/${encodeURIComponent(sessionId)}/booklet`,
            BACKEND_URL || window.location.origin,
        );
        u.searchParams.set('ticket', ticket);
        return u.toString();
    },

    // Email the booklet PDF via Resend. Un POST peut porter un en-tête : la
    // preuve se présente donc directement, sans jeton. C'était la porte la
    // plus grave des trois — elle n'exfiltrait pas la conversation d'un
    // autre, elle l'EXPÉDIAIT à l'adresse qu'on lui donnait.
    emailBooklet: ({ sessionId, sessionSecret = null, email, lang = 'fr', visitorName = '' }) =>
        http
            .post(`/medard/sessions/${encodeURIComponent(sessionId)}/email-booklet`, {
                email,
                lang,
                visitor_name: visitorName || null,
            }, { headers: sessionProofHeader(sessionSecret) })
            .then((r) => r.data),

    // Reservation deep-link (WhatsApp + mailto)
    reserve: (payload) =>
        // withStayToken: the C6 tier tag on Vincent's prefilled message must
        // see the Clef — otherwise every verified guest reads « Sans Clef ».
        http.post('/medard/concierge/reserve', withStayToken(payload)).then((r) => r.data),

    // `expertPersona` (Mode Expert v1) — the guest-owned Professeur register.
    // Rides as `expert_persona` (a NEW field: the legacy `mode` is deprecated
    // and ignored server-side — the tier is classifier-owned); the server
    // answers with the persona it actually served (AskResponse.persona / the
    // SSE start event), which is the only truth the UI displays.
    // `sessionSecret` — la preuve que cette session est bien la sienne (voir
    // `pairingSession.js` et backend/session_guard.py). Sans elle, une
    // session que le serveur connaît déjà n'est PAS continuée : il en ouvre
    // une neuve et la réponse porte le nouvel identifiant. Ce n'est donc pas
    // un champ facultatif de confort — c'est ce qui garde la mémoire de la
    // conversation d'un tour à l'autre.
    ask: ({ sessionId, sessionSecret = null, text, language = 'fr', monumentId = null, visitorName = null, mode = 'fast', expertPersona = false }) =>
        http
            .post('/medard/ask', withAccessTokens({
                session_id: sessionId,
                session_secret: sessionSecret,
                text,
                language,
                monument_id: monumentId,
                visitor_name: visitorName,
                mode,
                expert_persona: Boolean(expertPersona),
            }))
            .then((r) => r.data),

    /**
     * Sends audio Blob to backend for transcription.
     * Returns { transcript, language }.
     */
    transcribe: async (audioBlob, { language } = {}) => {
        const fd = new FormData();
        // Whisper detects the container from the filename — derive it from the
        // blob's REAL type (iPhone records m4a/mp4, not webm). See audioFormat.
        const ext = extForMimeType(audioBlob.type);
        fd.append('audio', audioBlob, `voice.${ext}`);
        if (language) fd.append('language', language);
        // ⚑ LA SEULE PORTE QUI N'EST PAS SOUS `http` — mesuré le 31/08/2026 :
        // 22 appels passent par l'instance interceptée, celui-ci est le seul
        // sur `axios` nu. Il échappe donc à `apiHealth` : ni sa réussite ni son
        // échec ne comptent.
        //
        // Ce n'est PAS un oubli à réparer, et c'est pour cela que la remarque
        // est écrite ici plutôt que corrigée. Le bandeau noir dit « Médard ne
        // répond pas pour l'instant — ce n'est pas votre connexion ». Or une
        // transcription en panne laisse Médard répondre parfaitement aux
        // questions ÉCRITES : brancher cette porte sur la santé générale
        // ferait dérouler ce bandeau sur une app qui marche, c'est-à-dire lui
        // ferait dire un mensonge — exactement le défaut que #847 a corrigé
        // dans l'autre sens. L'invité, lui, est servi : `VoiceChat` lui dit
        // que la reconnaissance vocale est indisponible et l'invite à écrire.
        //
        // (Le `timeout` plus court que les 120 s de `http` est l'autre raison
        // historique du contournement ; il se passerait en option par appel.)
        // Le nom de l'appareil, posé à la main : cette porte ne passe pas par
        // l'intercepteur de `http` (voir ci-dessus), et c'est la première
        // d'un tour vocal — sans lui, tout le Manoir y partagerait un seau.
        const res = await axios.post(`${API}/medard/transcribe`, fd, {
            headers: { 'Content-Type': 'multipart/form-data', ...clientKeyHeader() },
            timeout: 60000,
        });
        return res.data;
    },

    /**
     * « L'Œil de Médard » — identify a photographed monument.
     * Sends the JPEG photo (+ optional GPS fix) to the vision endpoint and
     * returns the structured scan result. Throws on !ok with `status`
     * attached so callers can map 413/415/429… to friendly messages.
     *
     * `takenOn` (optional 'YYYY-MM-DD') is the photograph's OWN calendar day
     * — a camera-roll scan reads it from EXIF (photoExif) so the passport
     * stamp can be dated by the day the shutter clicked, not the evening the
     * photo was shown. Sent only when the day is actually known.
     *
     * `sessionSecret` is the room's PROOF, and this call needs it because the
     * Eye WRITES: a recognised monument or treasure stamps the passport. Since
     * 18/08 the backend refuses to stamp a room the caller cannot prove and
     * mints a fresh one instead (Loi 3 — it never fails the recognition), so a
     * scan sent without the proof puts the guest's stamp in a room his app has
     * never heard of. It travels in the HEADER like every other proof in this
     * house — never as a FormData field, never in the URL: one channel, the
     * one the guard knows.
     */
    scan: async (photoBlob, { sessionId, sessionSecret = null, language = 'fr', lat, lon, takenOn, door, signal } = {}) => {
        const fd = new FormData();
        fd.append('photo', photoBlob, 'scan.jpg');
        if (sessionId) fd.append('session_id', sessionId);
        fd.append('language', language);
        if (Number.isFinite(lat)) fd.append('lat', String(lat));
        if (Number.isFinite(lon)) fd.append('lon', String(lon));
        if (typeof takenOn === 'string' && takenOn) fd.append('taken_on', takenOn);
        // Which of the Eye's two doors ('camera' | 'pellicule') — feeds ONE
        // aggregate day-counter (scan_porte) in the cockpit's Fréquentation
        // tab; the recognition itself never reads it. Nothing else travels.
        if (door === 'camera' || door === 'pellicule') fd.append('door', door);
        const res = await watchedFetch(`${API}/medard/scan`, {
            method: 'POST',
            body: fd,
            // No Content-Type here on purpose: the browser sets the multipart
            // boundary itself. Only the proof is added.
            headers: sessionProofHeader(sessionSecret),
            signal,
        });
        if (!res.ok) {
            const err = new Error(`scan failed: ${res.status}`);
            err.status = res.status;
            throw err;
        }
        return res.json();
    },

    /**
     * Stream Médard's reply via Server-Sent Events.
     *
     * The backend emits structured events as the LLM generates tokens. The
     * caller receives:
     *   • onStart({session_id, mode, persona, session_secret}) — `persona` is
     *     the SERVER's confirmation of the register it serves this turn (Mode
     *     Expert v1); the UI badge reads THIS, never the local preference.
     *     `session_id`/`session_secret` say which room the turn landed in —
     *     the caller MUST adopt them (see `pairingSession.rememberSession`)
     *   • onSpokenPartial(firstSentence) — first sentence of `spoken`; start
     *     TTS on it NOW, the rest is still being generated
     *   • onSpokenReady(spokenText)   — full spoken; TTS the remainder
     *   • onDelta(rawChunk)           — for live transcript / debug
     *   • onDone(askResponsePayload)  — full structured reply (book, etc.)
     *   • onCached(true|false)        — cache flag
     *   • onError(message)
     *
     * Returns a Promise resolved with the final AskResponse payload.
     */
    askStream: async ({
        sessionId,
        // La preuve de propriété de la session — voir `ask` juste au-dessus.
        sessionSecret = null,
        text,
        language = 'fr',
        monumentId = null,
        visitorName = null,
        mode = 'expert',
        expertPersona = false,
        speak = false,
        signal,
        onStart,
        onDelta,
        onSpokenPartial,
        onSpokenReady,
        onDone,
        onCached,
        onError,
    }) => {
        const res = await watchedFetch(`${API}/medard/ask/stream`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(withAccessTokens({
                session_id: sessionId,
                session_secret: sessionSecret,
                text,
                language,
                monument_id: monumentId,
                visitor_name: visitorName,
                mode,
                expert_persona: Boolean(expertPersona),
                // When true, the server may begin synthesizing the first
                // spoken sentence as soon as it is ready, shaving a round-trip
                // off time-to-first-word. Only set on turns the client will
                // actually speak (see MedardExperience) so no idle turn ever
                // triggers speculative ElevenLabs spend.
                speak: Boolean(speak),
            })),
            signal,
        });
        if (!res.ok || !res.body) {
            // Surface the HTTP status so the caller can tell a gentle « slow
            // down » (429) from a real failure and speak the right words.
            const err = new Error(`ask/stream failed: ${res.status}`);
            err.status = res.status;
            throw err;
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder('utf-8');
        let buffer = '';
        let finalPayload = null;

        // Parse the SSE protocol: `data: {...}\n\n`. Multiple events may
        // arrive in a single TCP chunk so we maintain a buffer.
        const processBuffer = () => {
            let idx;
            while ((idx = buffer.indexOf('\n\n')) !== -1) {
                const frame = buffer.slice(0, idx);
                buffer = buffer.slice(idx + 2);
                if (!frame.startsWith('data:')) continue;
                const json = frame.slice(5).trim();
                if (!json) continue;
                let ev;
                try {
                    ev = JSON.parse(json);
                } catch {
                    continue;
                }
                switch (ev.type) {
                    case 'start':
                        // `persona` — the server-confirmed register (Mode
                        // Expert v1): forwarded verbatim so the UI can show
                        // server truth, never the client's assumption.
                        //
                        // `session_id` + `session_secret` — the room this turn
                        // actually landed in. The backend re-mints both when a
                        // known session is presented without valid proof
                        // (backend/session_guard.py), so this event is the ONLY
                        // place the streaming door tells the client where it
                        // now stands. Drop it and the app keeps addressing a
                        // room nobody answers in: no error, no symptom, just a
                        // guest whose Médard forgets every sentence.
                        onStart?.({ session_id: ev.session_id, mode: ev.mode,
                                    persona: ev.persona,
                                    session_secret: ev.session_secret });
                        break;
                    case 'delta':
                        onDelta?.(ev.text || '');
                        break;
                    case 'spoken_partial':
                        onSpokenPartial?.(ev.spoken || '');
                        break;
                    case 'spoken_ready':
                        onSpokenReady?.(ev.spoken || '');
                        break;
                    case 'done':
                        // Surface the backend stage timings (observability)
                        // alongside the payload without touching its schema.
                        finalPayload = ev.timings
                            ? { ...ev.payload, timings: ev.timings }
                            : ev.payload;
                        if (ev.cached) onCached?.(true);
                        onDone?.(finalPayload);
                        break;
                    case 'cached':
                        onCached?.(true);
                        break;
                    case 'error':
                        onError?.(ev.message || 'Unknown server error');
                        break;
                    default:
                        // forward unknown event types verbatim
                        break;
                }
            }
        };

        // eslint-disable-next-line no-constant-condition
        while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            processBuffer();
        }
        // Flush any trailing partial frame (rare, but safe).
        buffer += decoder.decode();
        processBuffer();

        return finalPayload;
    },

    /**
     * Returns a Blob URL playable through an <audio> element.
     * Legacy: buffers entire MP3 before playback. Kept for retro-compat
     * (used by the kiosk legend reader).
     */
    speakUrl: async ({ text, voiceId, language = 'fr', signal }) => {
        const res = await watchedFetch(`${API}/medard/speak`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ text, voice_id: voiceId, language }),
            signal,
        });
        if (!res.ok) {
            throw speakFailure(res.status);
        }
        const blob = await res.blob();
        return URL.createObjectURL(blob);
    },

    /**
     * Plays Médard's voice via progressive MediaSource streaming — first audio
     * is heard within ~500 ms instead of waiting for the full MP3 (~3 s).
     *
     * iPhones never shipped classic MediaSource, but iOS 17.1+ exposes
     * ManagedMediaSource (same API + managed buffering) — used here so
     * recent iPhones also get progressive playback. Older iOS falls back
     * to the buffered Blob approach.
     *
     * Returns a Promise<HTMLAudioElement> resolved as soon as playback begins.
     * Exactly one of onEnded / onError fires afterwards: onError covers
     * element errors, mid-stream pump failures and rejected play() calls
     * (second arg: { audible } — whether anything was heard before dying).
     * `signal` aborts both the fetch and the progressive pump.
     */
    speakStream: async ({ text, voiceId, language = 'fr', audioEl, onEnded, onError, signal, fast = false }) => {
        const url = `${API}/medard/speak`;
        const t0 = performance.now();
        const mark = (label) =>
            console.debug(`[medard.timing] speak:${label} +${Math.round(performance.now() - t0)}ms`);
        // Prefer ManagedMediaSource when present (on iOS it is the ONLY MSE).
        const MSImpl =
            typeof window !== 'undefined'
                ? window.ManagedMediaSource || window.MediaSource || null
                : null;
        const supportsMSE =
            !!MSImpl &&
            typeof MSImpl.isTypeSupported === 'function' &&
            MSImpl.isTypeSupported('audio/mpeg');

        const audio = audioEl || new Audio();

        // The playback outcome fires exactly ONCE: either onEnded (clean
        // finish) or onError (element error, pump failure, rejected play()).
        // Without this, a mid-stream failure — endOfStream('network') fires
        // 'error', never 'ended' — left callers waiting forever.
        let settled = false;
        const settleEnd = () => {
            if (settled) return;
            settled = true;
            onEnded?.();
        };
        const settleError = (err) => {
            if (settled) return;
            settled = true;
            onError?.(err, { audible: (audio.currentTime || 0) > 0 });
        };

        // Always start the network request first so latency is unified.
        const res = await watchedFetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ text, voice_id: voiceId, language, fast }),
            signal,
        });
        if (!res.ok) {
            throw speakFailure(res.status);
        }
        mark('response_headers');

        // ---------- Fallback (no MediaSource) ----------
        if (!supportsMSE || !res.body) {
            const blob = await res.blob();
            const blobUrl = URL.createObjectURL(blob);
            audio.src = blobUrl;
            audio.onended = () => {
                URL.revokeObjectURL(blobUrl);
                settleEnd();
            };
            audio.onerror = () => {
                URL.revokeObjectURL(blobUrl);
                settleError(new Error('audio element error (blob)'));
            };
            await audio.play();
            mark('playing(blob)');
            return audio;
        }

        // ---------- Progressive streaming ----------
        const mediaSource = new MSImpl();
        if (typeof window !== 'undefined' && window.ManagedMediaSource &&
            mediaSource instanceof window.ManagedMediaSource) {
            // ManagedMediaSource refuses to open while remote playback
            // (AirPlay) is a possibility on the element.
            audio.disableRemotePlayback = true;
        }
        const objectUrl = URL.createObjectURL(mediaSource);
        audio.src = objectUrl;
        audio.onended = () => {
            URL.revokeObjectURL(objectUrl);
            settleEnd();
        };
        // endOfStream('network') and decode failures surface here — they fire
        // 'error' on the element and 'ended' never follows.
        audio.onerror = () => {
            URL.revokeObjectURL(objectUrl);
            settleError(audio.error || new Error('audio element error (mse)'));
        };

        await new Promise((resolve) => mediaSource.addEventListener('sourceopen', resolve, { once: true }));

        const sourceBuffer = mediaSource.addSourceBuffer('audio/mpeg');
        const reader = res.body.getReader();
        const queue = [];
        let reading = true;
        let started = false;

        const appendNext = () => {
            if (sourceBuffer.updating || !queue.length) return;
            try {
                sourceBuffer.appendBuffer(queue.shift());
            } catch (e) {
                console.warn('SourceBuffer append failed', e);
            }
        };
        sourceBuffer.addEventListener('updateend', () => {
            if (!started && audio.readyState >= 2) {
                started = true;
                mark('playing(mse)');
                // A rejected play() (lost gesture authorization — common on
                // iOS ManagedMediaSource) must notify the caller so it can
                // fall back, exactly like the blob path's `await play()`.
                audio.play().then(() => mark('play_ok')).catch((e) => {
                    console.warn('audio.play() rejected', e);
                    settleError(e);
                });
            }
            appendNext();
            if (!reading && !queue.length && mediaSource.readyState === 'open') {
                try { mediaSource.endOfStream(); } catch { /* noop */ }
            }
        });

        (async () => {
            let firstChunk = true;
            while (true) {
                const { done, value } = await reader.read();
                if (done) {
                    reading = false;
                    if (!sourceBuffer.updating && !queue.length && mediaSource.readyState === 'open') {
                        try { mediaSource.endOfStream(); } catch { /* noop */ }
                    }
                    break;
                }
                if (firstChunk) {
                    firstChunk = false;
                    mark('first_audio_byte');
                }
                queue.push(value);
                appendNext();
            }
        })().catch((e) => {
            console.warn('Stream pump failed', e);
            try { mediaSource.endOfStream('network'); } catch { /* noop */ }
            // The element 'error' event usually follows endOfStream('network'),
            // but not on every engine (nor if the source is already detached) —
            // settle directly; settleError is idempotent.
            settleError(e);
        });

        return audio;
    },

    /* createVoiceQueue (first-sentence TTS) is exported below, after this object. */

    /**
     * Open a WebSocket sync channel for lobby ↔ phone mirroring.
     * Returns an EventTarget-like wrapper with subscribe/close.
     */
    openSyncSocket: (sessionId, onMessage) => {
        const baseUrl = (BACKEND_URL || window.location.origin).replace(/^http/, 'ws');
        const url = `${baseUrl}/api/medard/sync/${encodeURIComponent(sessionId)}`;
        const ws = new WebSocket(url);
        ws.onmessage = (ev) => {
            try {
                const data = JSON.parse(ev.data);
                onMessage?.(data);
            } catch {
                /* ignore non-JSON */
            }
        };
        return ws;
    },
};

/**
 * Extract the in-progress `spoken` value from a partially streamed JSON
 * reply — powers the live subtitle while Médard's answer is still being
 * generated. Returns the decoded text streamed so far (may be empty), or
 * null when the `spoken` field hasn't opened yet.
 */
const SPOKEN_KEY_RE = /"spoken"\s*:\s*"/;
export function extractSpokenInProgress(raw) {
    const m = SPOKEN_KEY_RE.exec(raw);
    if (!m) return null;
    let out = '';
    let escape = false;
    for (let i = m.index + m[0].length; i < raw.length; i++) {
        const ch = raw[i];
        if (escape) {
            if (ch === 'n') out += '\n';
            else if (ch === 't') out += '\t';
            else if (ch === 'r') out += '\r';
            else if (ch === 'u') {
                const hex = raw.slice(i + 1, i + 5);
                if (/^[0-9a-fA-F]{4}$/.test(hex)) {
                    out += String.fromCharCode(parseInt(hex, 16));
                    i += 4;
                } else {
                    break; // incomplete \uXX… tail — the next delta completes it
                }
            } else out += ch; // \" \\ \/ …
            escape = false;
        } else if (ch === '\\') {
            escape = true;
        } else if (ch === '"') {
            break; // value closed
        } else {
            out += ch;
        }
    }
    return out;
}

/**
 * Tiny sequential audio queue for first-sentence TTS (spoken_partial).
 *
 * Clip 1 (the first sentence) plays immediately on the gesture-unlocked
 * shared <audio> element via progressive streaming. Clip 2 (the remainder,
 * known only when `spoken_ready` fires) is prefetched IN PARALLEL with
 * clip 1's playback as a Blob, then the same unlocked element is re-src'ed
 * on 'ended' — re-using the element keeps iOS Safari's gesture authorization.
 *
 * Seam contract:
 *   • clip 1 ends before clip 2 is downloaded  → wait, play on fetch resolve
 *   • clip 2 downloaded before clip 1 ends     → play instantly on 'ended'
 *   • no remainder (spoken == first sentence)  → caller invokes finish()
 *   • remainder fetch fails                    → onRemainderError(text) after
 *     clip 1 (caller may fall back to browser TTS), else just end.
 *   • clip 1 fails (element error, pump death, rejected play()) → route to
 *     the remainder when it exists; if nothing else will play and clip 1
 *     was never heard, onRemainderError(firstText) → caller's browser-TTS
 *     fallback. The queue ALWAYS terminates — onEnded or onRemainderError
 *     fires exactly once, the UI can never stick in 'speaking'.
 *   • stop() at any point                      → silence + no callbacks
 */
export function createVoiceQueue({ audioEl, language = 'fr', voiceId, onEnded, onRemainderError, richOpener = false } = {}) {
    // `richOpener` — the Professeur speaks ENTIRELY with the rich TTS model
    // (owner, 2026-08-02: the fast-opener flash model has a slightly
    // different grain/pace and weaker French — it fumbled « Autun »,
    // « Bibracte » and dates precisely in the register whose directive
    // front-loads names and dates into the first two sentences). The
    // Conteur keeps the low-latency opener; Clef holders trade ~a second
    // of first-word latency for a voice that never slips.
    const audio = audioEl || getSharedAudio();
    // One controller for the queue's whole lifetime: stop() aborts clip 1's
    // progressive pump AND the in-flight remainder prefetch — an interrupted
    // guest must not keep paying for a download nobody will hear.
    const abort = new AbortController();
    let stopped = false;
    let firstEnded = false;
    let firstFailed = false;        // clip 1 died (error ≠ clean 'ended')
    let firstAudible = false;       // clip 1 produced audible playback
    let firstText = null;           // clip 1 text — browser-TTS fallback
    let finished = false;           // spoken_ready arrived, nothing left to say
    let remainderUrl = null;        // prefetched Blob URL of clip 2
    let remainderPending = false;   // clip 2 fetch in flight
    let remainderFailedText = null; // clip 2 fetch failed — text for fallback
    let settled = false;            // a terminal callback already fired

    const endAll = () => {
        if (stopped || settled) return;
        settled = true;
        onEnded?.();
    };

    // Hand `text` to the caller's browser-TTS fallback, else just end —
    // either way the queue terminates, exactly once.
    const failOut = (text) => {
        if (stopped || settled) return;
        if (onRemainderError && text) {
            settled = true;
            onRemainderError(text);
        } else {
            endAll();
        }
    };

    const maybePlayNext = () => {
        if (stopped || !firstEnded) return;
        if (remainderUrl) {
            const url = remainderUrl;
            remainderUrl = null;
            // Handlers BEFORE src: 'ended' of clip 1 already fired, these
            // now belong to clip 2.
            audio.onended = () => { URL.revokeObjectURL(url); endAll(); };
            audio.onerror = () => { URL.revokeObjectURL(url); endAll(); };
            audio.src = url;
            audio.play().catch(() => { URL.revokeObjectURL(url); endAll(); });
            return;
        }
        if (remainderFailedText) {
            const text = remainderFailedText;
            remainderFailedText = null;
            failOut(text);
            return;
        }
        if (finished && !remainderPending) {
            // Nothing left to play. If clip 1 died before a single audible
            // frame, the guest heard NOTHING — offer the first sentence to
            // the caller's fallback voice instead of ending silently.
            if (firstFailed && !firstAudible && firstText) failOut(firstText);
            else endAll();
        }
        // else: clip 2 still downloading, or spoken_ready not arrived — wait.
    };

    return {
        /** Start speaking the first sentence now (progressive stream). */
        async playFirst(text) {
            firstText = text;
            await medardApi.speakStream({
                text,
                voiceId,
                language,
                audioEl: audio,
                signal: abort.signal,
                // The latency-critical opener → low-latency model (the ONLY
                // caller that sets fast; the remainder + canon stay rich).
                // Except the Professeur (richOpener): one voice, no seam.
                fast: !richOpener,
                onEnded: () => {
                    firstAudible = firstAudible || (audio.currentTime || 0) > 0;
                    firstEnded = true;
                    maybePlayNext();
                },
                // Clip 1 failed mid-flight (MSE pump death, element error,
                // rejected play()). Treat it as ended and let maybePlayNext
                // route: remainder if available, else fallback/termination.
                onError: (e, info) => {
                    if (stopped) return;
                    console.warn('First-sentence clip failed', e);
                    firstAudible = firstAudible || Boolean(info?.audible)
                        || (audio.currentTime || 0) > 0;
                    firstFailed = true;
                    firstEnded = true;
                    maybePlayNext();
                },
            });
        },

        /** Queue the remainder — downloads in parallel with clip 1. */
        enqueueRemainder(text) {
            if (stopped) return;
            if (!text || !text.trim()) {
                finished = true;
                maybePlayNext();
                return;
            }
            remainderPending = true;
            finished = true; // the remainder is the last thing to say
            medardApi
                .speakUrl({ text, voiceId, language, signal: abort.signal })
                .then((url) => {
                    remainderPending = false;
                    if (stopped) {
                        URL.revokeObjectURL(url);
                        return;
                    }
                    remainderUrl = url;
                    maybePlayNext();
                })
                .catch((e) => {
                    remainderPending = false;
                    if (stopped) return;
                    console.warn('Remainder TTS prefetch failed', e);
                    remainderFailedText = text;
                    maybePlayNext();
                });
        },

        /** Declare that no remainder will come (spoken == first sentence). */
        finish() {
            finished = true;
            maybePlayNext();
        },

        /** True once clip 1 has produced audible playback (billing guard —
         *  callers must not re-synthesize the FULL spoken after the guest
         *  already heard sentence 1). */
        hasPlayedAudibly() {
            return firstAudible || (audio.currentTime || 0) > 0;
        },

        stop() {
            stopped = true;
            // Cancel clip 1's progressive pump and the in-flight remainder
            // prefetch — no orphaned downloads after an interruption.
            try { abort.abort(); } catch { /* noop */ }
            try {
                audio.pause();
                audio.currentTime = 0;
            } catch { /* noop */ }
            // Clear OUR handlers so a stale element event can't leak into
            // the next turn (the shared <audio> element is reused).
            audio.onended = null;
            audio.onerror = null;
            // Release every object URL this queue created: the remainder's
            // prefetched blob and whatever clip is loaded in the element
            // (blob MP3 or MediaSource handle — revoking also detaches it).
            const src = audio.src || '';
            try { audio.removeAttribute('src'); } catch { /* noop */ }
            if (src.startsWith('blob:')) {
                try { URL.revokeObjectURL(src); } catch { /* noop */ }
            }
            if (remainderUrl) {
                URL.revokeObjectURL(remainderUrl);
                remainderUrl = null;
            }
        },
    };
}
